import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import PandaDocModal from './PandaDocModal';

export default function HostTemplatePicker({ host, program, user, programName, onDraft, onSent, disabled }) {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState('');
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [agreementOpen, setAgreementOpen] = useState(false);
  const template = templates.find(t => t.trigger_type === selected);
  const needsStudent = template?.mode === 'student-workflow' || template?.mode === 'student-draft';
  const privileged = user.role === 'admin' || user.is_host_specialist;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    api.get('/hosts/message-templates', { params: { includeWorkflows: 'true' } })
      .then(res => { if (!cancelled) setTemplates(res.data); })
      .catch(() => { if (!cancelled) setError('Could not load host templates.'); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setSelected(''); setStudentId(''); setNotice(''); setError('');
  }, [host.id, program]);

  useEffect(() => {
    if (!needsStudent) return;
    let cancelled = false;
    setBusy(true); setStudents([]); setStudentId('');
    api.get('/students', { params: { archived: 'false' } })
      .then(res => {
        if (cancelled) return;
        setStudents(res.data.filter(s => {
          if (s.program_code !== program) return false;
          if (selected === 'host_outreach') return ['pre_req', 'ready'].includes(s.status);
          if (String(s.host_id) !== String(host.id)) return false;
          if (selected === 'practicum_start_reminder_host') return ['active', 'completing'].includes(s.status) && s.practicum_start_date;
          if (selected === 'interview_booked_host') return ['host_pending_response', 'interview_scheduling', 'active'].includes(s.status);
          return ['host_pending_response', 'interview_scheduling'].includes(s.status);
        }));
      })
      .catch(() => { if (!cancelled) setError('Could not load students. Select the template again to retry.'); })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [needsStudent, selected, host.id, program]);

  async function applyDraft() {
    setBusy(true); setError(''); setNotice('');
    try {
      const res = await api.get(`/hosts/${host.id}/message-preview`, {
        params: { trigger: selected, program_code: program, student_id: studentId || undefined },
      });
      if (!mounted.current) return;
      onDraft(res.data);
      setNotice('Personalized draft loaded below. Review it before sending.');
    } catch (err) { if (mounted.current) setError(err.response?.data?.error || 'Could not personalize this template.'); }
    finally { if (mounted.current) setBusy(false); }
  }

  async function sendAvailability() {
    setBusy(true); setError(''); setNotice('');
    try {
      await api.post(`/hosts/${host.id}/availability-check`);
      setNotice('Availability check-in sent with the secure response link.');
      onSent?.();
    } catch (err) { setError(err.response?.data?.error || 'Could not send the availability check-in.'); }
    finally { setBusy(false); }
  }

  return (
    <div style={{ padding: 12, border: '1px solid var(--line)', borderRadius: 8 }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
        Send via template
        <select className="input" value={selected} disabled={busy || disabled}
          onChange={e => { setSelected(e.target.value); setError(''); setNotice(''); }}>
          <option value="">Choose a host template…</option>
          {templates.map(t => <option key={t.trigger_type} value={t.trigger_type}>{t.label}</option>)}
        </select>
      </label>
      {template && <>
        <p style={{ fontSize: 12, color: 'var(--ink-3)', margin: '8px 0' }}>{template.description}</p>
        {needsStudent && (
          <label style={{ fontSize: 13 }}>Student
            <select className="input" value={studentId} disabled={busy || disabled} onChange={e => setStudentId(e.target.value)}>
              <option value="">{busy ? 'Loading students…' : 'Select a student…'}</option>
              {students.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name} — {s.email}</option>)}
            </select>
          </label>
        )}
        {needsStudent && !busy && !students.length && <p style={{ fontSize: 12 }}>No eligible students in this program. Student introductions need an unplaced student; interview and reminder emails need a student assigned to this host.</p>}
        {['draft', 'student-draft'].includes(template.mode) && (
          <button className="btn btn-secondary btn-sm" disabled={busy || disabled || !program || (needsStudent && !studentId)} onClick={applyDraft}>
            {busy ? 'Loading…' : 'Load personalized draft'}
          </button>
        )}
        {template.mode === 'student-workflow' && studentId && (
          <Link className="btn btn-secondary btn-sm" to={`/students/${studentId}?workflow=host&host_id=${host.id}&host_template=${selected}`}>
            Open student workflow
          </Link>
        )}
        {template.mode === 'agreement' && (privileged ? (
          <button className="btn btn-secondary btn-sm" disabled={busy || disabled} onClick={() => setAgreementOpen(true)}>Prepare agreement in PandaDoc</button>
        ) : <p style={{ fontSize: 12 }}>An administrator or Host Specialist must prepare this agreement.</p>)}
        {template.mode === 'availability' && (privileged ? (
          <button className="btn btn-primary btn-sm" disabled={busy || disabled} onClick={sendAvailability}>
            {busy ? 'Sending…' : 'Send availability check-in'}
          </button>
        ) : <p style={{ fontSize: 12 }}>An administrator or Host Specialist must send the secure availability check-in.</p>)}
      </>}
      {notice && <p role="status" style={{ fontSize: 12 }}>{notice}</p>}
      {error && <p role="alert" style={{ fontSize: 12, color: '#B3261E' }}>{error}</p>}
      {agreementOpen && <PandaDocModal host={host} programs={[{ code: program, name: programName }]}
        onClose={() => setAgreementOpen(false)} onSent={onSent} />}
    </div>
  );
}
